import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', formData);
      login(response.data.token, response.data.user);
      navigate('/schools');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      const errorCode = err.response?.data?.errorCode;
      
      // Special handling for unregistered users
      if (errorCode === 'USER_NOT_FOUND' || errorMessage.includes('New to SLN Book World')) {
        setError('No account found with this email. New to SLN Book World? Please create an account to get started.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div 
        className="page login-page"
        style={{
          backgroundImage: 'url(/login.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Animated Background Elements */}
        <div className="auth-animated-bg">
          <div className="auth-floating-shape auth-shape-1"></div>
          <div className="auth-floating-shape auth-shape-2"></div>
          <div className="auth-floating-shape auth-shape-3"></div>
          <div className="auth-floating-shape auth-shape-4"></div>
          <div className="auth-gradient-orb auth-orb-1"></div>
          <div className="auth-gradient-orb auth-orb-2"></div>
        </div>

        <div className="container">
          <div className="auth-container">
            <div className="auth-header">
              <div className="auth-icon-wrapper">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h1 className="auth-title">Welcome Back</h1>
              <p className="auth-subtitle">Sign in to continue to SLN Book World</p>
            </div>

            {error && (
              <div className="auth-error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span>{error}</span>
                  {error.includes('register first') && (
                    <Link 
                      to="/register" 
                      style={{ 
                        color: 'var(--primary-color, #007bff)', 
                        textDecoration: 'underline',
                        fontWeight: '500',
                        marginTop: '4px'
                      }}
                    >
                      Click here to register →
                    </Link>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-group floating-label">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  Email Address
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <div className="auth-form-group floating-label">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder=" "
                  className="auth-form-input"
                />
                <label className="auth-form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Password
                </label>
                <div className="auth-input-underline"></div>
              </div>

              <button type="submit" className="auth-btn-submit" disabled={loading}>
                {loading ? (
                  <>
                    <svg className="auth-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
                    </svg>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>New to SLN Book World?</span>
            </div>

            <p className="auth-link">
              Don't have an account? <Link to="/register">Create one now</Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;

